import { Box, Button } from "@mui/material";
import { useAtomValue } from "jotai";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { CustomTemplates } from "../../pages/create-content/use-custom-templates";
import { toBase64 } from "../../utils/base64";
import { useAnalytics } from "../analytics/analytics";
import type { FontSelectionSummary } from "../brand-kit/hooks/use-font-selections";
import type { BrandTone } from "../brand-kit/server/brand-tone-service";
import type { Account } from "../generic/server/supabase-account-service";
import { previewsFamily } from "../multiformat/store/previews-family";
import type { MultiformatDimensions } from "../multiformat/store/types";
import { ContentPreviewImage } from "./content-preview-image";

export const ContentPreviews: FC<{
  contentTypeName: string;
  answer: string;
  useSelectedLogo: boolean;
  dimensions: MultiformatDimensions;
  customTemplates: CustomTemplates | null;
  account: Account;
  fontSelections: FontSelectionSummary[] | null;
  db: string | null;
  fileId?: string;
  imageStyles?: string[];
  brandTones: BrandTone[] | null;
  tone?: string;
}> = ({
  contentTypeName,
  answer,
  useSelectedLogo,
  dimensions,
  customTemplates,
  account,
  fontSelections,
  db,
  fileId,
  imageStyles,
  brandTones,
  tone,
}) => {
  const navigate = useNavigate();
  const { gaEvent } = useAnalytics();
  const { t } = useTranslation();

  const previews = useAtomValue(
    previewsFamily({
      contentTypeName,
      answer,
      customTemplates,
      useSelectedLogo,
      fontSelections: fontSelections || [],
      industry: account.industry ?? "Technology && IT",
      db: db || undefined,
      fileId,
      imageStyles,
      brandTones: brandTones || [],
      tone,
    }),
  );

  const [numItems, setNumItems] = useState(4);
  const itemsPerPage = 4;
  const numPreviews = previews?.length ?? 0;

  useEffect(() => {
    if (numPreviews > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setNumItems(Math.min(numPreviews, itemsPerPage));
    }
  }, [answer, numPreviews]);

  const loadMore = useRef<HTMLButtonElement>(null);

  const handleLoadMore = useCallback(() => {
    gaEvent("multiformat_button", { action: "click", value: "Load More" });

    setNumItems((prev) => Math.min(prev + itemsPerPage, numPreviews));
    setTimeout(() => {
      loadMore.current?.scrollIntoView({ behavior: "smooth" });
    }, 200);
  }, [gaEvent, numPreviews]);

  const handleEdit = useCallback(
    (i: number) => {
      gaEvent("multiformat_button", {
        action: "click",
        value: `Edit Position ${i + 1}`,
      });

      if (previews?.[i]!.state === "hasData" && previews?.[i].data) {
        const preview = previews[i].data.preview;
        const d = toBase64(
          JSON.stringify({
            contentType: contentTypeName,
            customizations: preview.customizations,
            page: preview.page_number,
            templateCode: preview.template_code,
            customTemplate: previews[i].data.customTemplate,
          }),
        );

        const params = new URLSearchParams({
          d,
          title: preview.title && preview.title !== "" ? preview.title : contentTypeName,
        });
        void navigate({ pathname: "/create-content/edit", search: params.toString() });
      }
    },
    [gaEvent, previews, contentTypeName, navigate],
  );

  return (
    <>
      <Box
        sx={[
          (theme) => ({
            display: "grid",
            gap: 2,
            [theme.breakpoints.down("xs")]: {
              gridTemplateColumns: "1fr",
            },
            [theme.breakpoints.up("sm")]: {
              gridTemplateColumns: "repeat(2, 1fr)",
            },
            [theme.breakpoints.up("lg")]: {
              gridTemplateColumns: "repeat(2, 1fr)",
            },
          }),
        ]}
      >
        {previews
          ?.filter((p) => p !== undefined)
          .map((preview, i) => (
            <ContentPreviewImage
              key={i}
              image={preview}
              onEdit={() => handleEdit(i)}
              hidden={i >= numItems}
              designer={account.designer ?? null}
              dimensions={dimensions}
            />
          ))}
      </Box>
      {numItems < numPreviews && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
          <Button
            data-analytics-id="design-huddle-content-previews-load-more"
            onClick={handleLoadMore}
            ref={loadMore}
            variant="contained"
            color="primary"
            size="large"
            loading={false}
          >
            {t("Load More")}
          </Button>
        </Box>
      )}
    </>
  );
};
